import dayjs, { Dayjs } from "dayjs";

export interface DateRangePreset {
  id: string;
  label: string;
  /** Evaluated on every render/compare, so "today" stays correct across a midnight rollover. */
  range: () => [Dayjs, Dayjs];
}

const day = (d: Dayjs) => d.startOf("day");
const today = () => day(dayjs());
const startOfWeekMon = (d: Dayjs) => d.subtract((d.day() + 6) % 7, "day");
const quarterStart = (d: Dayjs) => d.month(Math.floor(d.month() / 3) * 3).startOf("month");

/**
 * The individual presets, addressable by name so a page can assemble its own rail —
 * e.g. `[[DATE_PRESETS.tm, DATE_PRESETS.lm], [DATE_PRESETS.q]]` for a monthly report.
 */
export const DATE_PRESETS = {
  today: { id: "today", label: "วันนี้", range: () => [today(), today()] },
  yesterday: {
    id: "yst",
    label: "เมื่อวาน",
    range: () => [day(dayjs().subtract(1, "day")), day(dayjs().subtract(1, "day"))],
  },
  last7: {
    id: "7",
    label: "7 วันล่าสุด",
    range: () => [day(dayjs().subtract(6, "day")), today()],
  },
  thisWeek: {
    id: "tw",
    label: "สัปดาห์นี้",
    range: () => [day(startOfWeekMon(dayjs())), today()],
  },
  lastWeek: {
    id: "lw",
    label: "สัปดาห์ที่แล้ว",
    range: () => {
      const start = day(startOfWeekMon(dayjs()).subtract(7, "day"));
      return [start, start.add(6, "day")];
    },
  },
  last30: {
    id: "30",
    label: "30 วันล่าสุด",
    range: () => [day(dayjs().subtract(29, "day")), today()],
  },
  thisMonth: {
    id: "tm",
    label: "เดือนนี้",
    range: () => [dayjs().startOf("month"), today()],
  },
  lastMonth: {
    id: "lm",
    label: "เดือนที่แล้ว",
    range: () => {
      const start = dayjs().subtract(1, "month").startOf("month");
      return [start, day(start.endOf("month"))];
    },
  },
  thisQuarter: {
    id: "q",
    label: "ไตรมาสนี้",
    range: () => [quarterStart(dayjs()), today()],
  },
  thisYear: { id: "ty", label: "ปีนี้", range: () => [dayjs().startOf("year"), today()] },
  lastYear: {
    id: "ly",
    label: "ปีที่แล้ว",
    range: () => {
      const start = dayjs().subtract(1, "year").startOf("year");
      return [start, day(start.endOf("year"))];
    },
  },
} satisfies Record<string, DateRangePreset>;

/**
 * "ทั้งหมดที่มี" has no natural lower bound, so the caller supplies one. Pass the widest
 * span the page's data source will actually serve — for an API with a span cap, that cap.
 * Labelling an arbitrary window "all time" is the whole trap this parameter exists to avoid.
 */
export const allTimePreset = (lookbackDays: number): DateRangePreset => ({
  id: "all",
  label: "ทั้งหมดที่มี",
  range: () => [day(dayjs().subtract(lookbackDays, "day")), today()],
});

/**
 * The standard four-group rail: day, week, month, year. Pass `allTimeLookbackDays` to append
 * "ทั้งหมดที่มี" to the last group; omit it and the preset is left out entirely.
 */
export const defaultPresetGroups = (
  options: { allTimeLookbackDays?: number } = {},
): DateRangePreset[][] => [
  [DATE_PRESETS.today, DATE_PRESETS.yesterday, DATE_PRESETS.last7],
  [DATE_PRESETS.thisWeek, DATE_PRESETS.lastWeek, DATE_PRESETS.last30],
  [DATE_PRESETS.thisMonth, DATE_PRESETS.lastMonth, DATE_PRESETS.thisQuarter],
  [
    DATE_PRESETS.thisYear,
    DATE_PRESETS.lastYear,
    ...(options.allTimeLookbackDays !== undefined
      ? [allTimePreset(options.allTimeLookbackDays)]
      : []),
  ],
];

/**
 * The default rail without its quarter and year shortcuts, so no shortcut spans more than a
 * month (31 days).
 */
export const WITHIN_A_MONTH_PRESET_GROUPS: DateRangePreset[][] = [
  [DATE_PRESETS.today, DATE_PRESETS.yesterday, DATE_PRESETS.last7],
  [DATE_PRESETS.thisWeek, DATE_PRESETS.lastWeek, DATE_PRESETS.last30],
  [DATE_PRESETS.thisMonth, DATE_PRESETS.lastMonth],
];

/**
 * E-Tax rail. Submission runs on the Revenue Department's MONTHLY clock (a closed month has
 * to reach the provider by the 15th of the next one), so the month presets lead and the
 * day/week shortcuts of the default rail are trimmed to the two spans finance actually uses
 * for a spot check. No "ทั้งหมดที่มี": the candidate query spans every bill ever recorded.
 */
export const etaxPresetGroups = (): DateRangePreset[][] => [
  [DATE_PRESETS.last7, DATE_PRESETS.last30],
  [DATE_PRESETS.thisMonth, DATE_PRESETS.lastMonth, DATE_PRESETS.thisQuarter],
];
